import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ClipboardList, Bookmark, Factory, Truck, Archive, User,
  Boxes, Clipboard, ArrowUp, Home, Package, Wrench, Sparkles,
  Bike, ChevronDown, Menu, X, Settings, LogOut, BarChart2,
  Barcode, Upload, ShoppingBag, Calendar, Award, FileText, Mail,
  DollarSign, ToggleLeft, ToggleRight, Bell, MessageSquare, GraduationCap
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { suppliesService } from "../../services/suppliesService";
import OperatorSwitcher from "../oficina/OperatorSwitcher";
import { useCommunication } from "../../contexts/CommunicationContext";

// ─── Mapeamento de Ícones (mantido idêntico ao original) ─────────────
const TAB_ICONS = {
  dashboard: BarChart2, stock: ClipboardList, supplies: Package,
  reservations: Bookmark, production: Factory, orders: Truck,
  transit: ArrowUp, stock_production: Boxes, reports: Clipboard,
  archived: Archive, config: Settings, oficina: Wrench,
  novos_produtos: Sparkles, motoboy: Bike, home: Home,
  conference: Barcode, sales: Upload, pdv: ShoppingBag,
  planejamento: Calendar, pontuacao: Award, colaboradores: User,
  fiscal: FileText, postagem: Mail, financeiro: DollarSign,
  estoque_area: ClipboardList, produto: Package, treinamentos: GraduationCap
};

// ─── Labels — EXPORTS OBRIGATÓRIOS (HomeTab.js depende destes) ───────
export const TAB_LABELS = {
  dashboard: "DASHBOARD",   stock: "ESTOQUE",       supplies: "INSUMOS",
  reservations: "RESERVAS", production: "PRODUÇÃO",  orders: "PEDIDOS",
  stock_production: "FÁBRICA ESTOQUE",  archived: "ARQUIVO",
  transit: "TRÂNSITO",     reports: "LOGS",          config: "CONFIG",
  oficina: "OFICINA",      novos_produtos: "PRODUTOS", motoboy: "MOTOBOY",
  pdv: "PDV SHOWROOM",     planejamento: "PLANEJAMENTO",
  pontuacao: "PONTOS & BÔNUS", colaboradores: "COLABORADORES",
  treinamentos: "TREINAMENTOS",
  fiscal: "ÁREA FISCAL",   postagem: "EXPEDIÇÃO CORREIOS",
  financeiro: "FINANCEIRO", estoque_area: "ESTOQUE", produto: "FICHA DO PRODUTO"
};
export const FULL_TAB_LABELS = { home: "INÍCIO", ...TAB_LABELS };

// ─── Definição dos grupos ─────────────────────────────────────────────
export const GROUPS = [
  { id: "operacoes",  label: "Operações",       color: "#3B82F6",
    tabs: ["production", "orders", "postagem", "reservations", "transit", "motoboy"] },
  { id: "estoque",    label: "Estoque & Mat.",   color: "#10B981",
    tabs: ["stock", "stock_production", "supplies", "planejamento"] },
  { id: "criacao",    label: "Criação",           color: "#A78BFA",
    tabs: ["oficina", "novos_produtos"] },
  { id: "gestao",     label: "Gestão",            color: "#FBBF24",
    tabs: ["dashboard", "financeiro", "colaboradores", "treinamentos", "pontuacao", "fiscal", "reports", "archived"] },
];

// ─── Componente principal ─────────────────────────────────────────────
export default function Sidebar({ user, activeTab, setActiveTab, hasAccess, logout, modoClassico, toggleModoClassico }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const [metalQuotes, setMetalQuotes] = useState(null);
  const {
    openDrawer,
    unreadNotifications,
    unreadChatCount,
    pendingTasksCount,
  } = useCommunication() || {};

  useEffect(() => {
    let active = true;
    const fetchQuotes = async () => {
      try {
        const res = await suppliesService.syncMetalQuotes();
        if (res && active) {
          setMetalQuotes(res);
        } else if (!res) {
          console.warn("Sidebar: syncMetalQuotes retornou valor nulo/falso");
        }
      } catch (err) {
        console.error("Erro ao carregar cotações na sidebar:", err);
      }
    };
    fetchQuotes();
    // Atualiza a cada 30 minutos
    const interval = setInterval(fetchQuotes, 30 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Fecha dropdown ao clicar fora do header
  useEffect(() => {
    if (!openGroup) return;
    const handle = (e) => {
      if (navRef.current && !navRef.current.contains(e.target))
        setOpenGroup(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [openGroup]);

  // Fecha com tecla Escape
  useEffect(() => {
    const handle = (e) => {
      if (e.key === "Escape") { setOpenGroup(null); setMobileOpen(false); }
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, []);

  // Regras de acesso
  const canAccess = (tabId) => {
    if (tabId === "colaboradores" || tabId === "treinamentos") return true; // Todos os colaboradores logados podem ver seu próprio ponto e treinamentos
    if (tabId === "dashboard") return user?.role === "master" || hasAccess("dashboard");
    if (tabId === "transit") return hasAccess("transit") || hasAccess("production") || hasAccess("orders");
    if (tabId === "postagem") return hasAccess("postagem") || hasAccess("production") || hasAccess("orders");
    if (tabId === "pontuacao") return hasAccess("pontuacao");
    if (tabId === "archived") return hasAccess("archived");
    if (tabId === "estoque_area") return hasAccess("stock") || hasAccess("stock_production") || hasAccess("planejamento");
    return hasAccess(tabId);
  };

  const dynamicGroups = useMemo(() => {
    return GROUPS.map((g) => {
      let tabs = g.tabs;
      if (g.id === "estoque") {
        if (modoClassico) {
          tabs = ["stock", "stock_production", "supplies", "planejamento"];
        } else {
          tabs = ["estoque_area", "supplies"];
        }
      }
      return { ...g, tabs };
    });
  }, [modoClassico]);

  // Filtra grupos e tabs por permissão
  const visibleGroups = useMemo(() => {
    return dynamicGroups
      .map((g) => ({ ...g, tabs: g.tabs.filter(canAccess) }))
      .filter((g) => g.tabs.length > 0);
  }, [dynamicGroups, user, hasAccess]);

  // Seleção de aba — fecha dropdown e drawer
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setOpenGroup(null);
    setMobileOpen(false);
  };

  // Grupo que contém a aba ativa (para highlight visual)
  const activeGroupId = useMemo(() => {
    return dynamicGroups.find((g) => g.tabs.includes(activeTab))?.id || null;
  }, [dynamicGroups, activeTab]);

  return (
    <>
      {/* ══ TOPBAR ══════════════════════════════════════════════════════ */}
      <header
        ref={navRef}
        className="flex items-center justify-between w-full h-14 bg-slate-900
                   text-white sticky top-0 z-50 px-3 lg:px-4 shrink-0 shadow-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="h-9 w-9 bg-white rounded flex items-center justify-center overflow-hidden">
            <img
              src="https://www.semprejoias.com.br/favicon/34692/sjoiasfav.png"
              alt="Logo" className="w-full h-full object-contain"
            />
          </div>
          <div className="hidden md:flex flex-col whitespace-nowrap">
            <h1 className="font-bold text-sm leading-tight">Gestão de Processos</h1>
            <span className="text-[10px] text-slate-400">Sempre Joias v1.1</span>
          </div>
        </div>

        {/* ── NAV DESKTOP (lg+) ────────────────────────────────────── */}
        <nav className="hidden lg:flex items-center gap-1 mx-4 h-full">

          {/* Início */}
          <button
            onClick={() => handleTabChange("home")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
              rounded-md transition-all whitespace-nowrap
              ${activeTab === "home"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
          >
            <Home size={14} /> Início
          </button>

          {/* PDV standalone */}
          {canAccess("pdv") && (
            <button
              onClick={() => handleTabChange("pdv")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                rounded-md transition-all whitespace-nowrap
                ${activeTab === "pdv"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
            >
              <ShoppingBag size={14} /> PDV Showroom
            </button>
          )}

          {/* Grupos */}
          {visibleGroups.map((g) => {
            const isOpen = openGroup === g.id;
            const isActive = activeGroupId === g.id;
            return (
              <div key={g.id} className="relative">
                <button
                  onClick={() => setOpenGroup(isOpen ? null : g.id)}
                  style={isOpen
                    ? { background: `${g.color}22`, color: g.color, borderColor: `${g.color}44` }
                    : {}}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                    rounded-md transition-all whitespace-nowrap border
                    ${isActive && !isOpen
                      ? "bg-slate-700 text-white border-transparent"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white border-transparent"}`}
                >
                  {g.label}
                  <ChevronDown size={12}
                    style={{ transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "none" }}
                  />
                </button>

                {/* Dropdown */}
                {isOpen && (
                  <div
                    className="absolute top-[calc(100%+6px)] left-0 bg-slate-800
                               border border-slate-700 rounded-xl p-3 shadow-2xl"
                    style={{ minWidth: 220, borderColor: `${g.color}33`, zIndex: 60 }}
                  >
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2 px-1"
                       style={{ color: g.color }}>{g.label}</p>
                    <div className="flex flex-col gap-1">
                      {g.tabs.map((tabId) => {
                        const Icon = TAB_ICONS[tabId] || ClipboardList;
                        const isTabActive = activeTab === tabId;
                        return (
                          <button key={tabId}
                            onClick={() => handleTabChange(tabId)}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg
                              text-xs font-semibold text-left transition-all
                              ${isTabActive
                                ? "bg-blue-600 text-white"
                                : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}
                          >
                            <Icon size={14}
                              style={isTabActive ? {} : { color: g.color, opacity: 0.8 }} />
                            {FULL_TAB_LABELS[tabId]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Config standalone */}
          {canAccess("config") && (
            <button
              onClick={() => handleTabChange("config")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                rounded-md transition-all ml-1
                ${activeTab === "config"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
            >
              <Settings size={14} /> Config
            </button>
          )}
          {/* Toggle Modo Clássico / Novo */}
          {(hasAccess("stock") || hasAccess("stock_production") || hasAccess("planejamento")) && (
            <button
              onClick={() => toggleModoClassico(!modoClassico)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ml-2 border border-slate-700 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white cursor-pointer"
              title={modoClassico ? "Ativar nova Área de Estoque Integrada" : "Voltar para o menu clássico"}
            >
              {modoClassico ? (
                <>
                  <ToggleRight size={14} className="text-slate-400" />
                  <span>Modo Clássico</span>
                </>
              ) : (
                <>
                  <ToggleLeft size={14} className="text-emerald-400" />
                  <span className="text-emerald-400 font-bold">Área Integrada</span>
                </>
              )}
            </button>
          )}
        </nav>

        {/* ── Hamburger (mobile/tablet) ─────────────────────────────── */}
        <button
          className="lg:hidden p-2 rounded-lg text-slate-400 hover:bg-slate-800
                     hover:text-white transition-colors ml-auto mr-2"
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>

        {/* ── PAINEL DA DIREITA (Cotações + Usuário) ── */}
        <div className="flex items-center gap-3 md:gap-4 shrink-0 h-full ml-auto">
          {/* COTAÇÃO DE METAIS */}
          {metalQuotes && (
            <div className="hidden md:flex items-center gap-2.5 bg-slate-800/80 px-2.5 py-1.5 rounded-lg border border-slate-700/50 text-[11px] font-bold shrink-0">
              {/* Ouro */}
              <div className="flex items-center gap-1.5">
                <span className="text-yellow-500 font-black">Ouro:</span>
                <span className="text-slate-200 font-mono">R$ {metalQuotes.gold.price.toFixed(2)}/g</span>
                {Number(metalQuotes.gold.pctChange) >= 0 ? (
                  <span className="text-emerald-400 flex items-center text-[9px] gap-0.5" title={`Alta de ${metalQuotes.gold.pctChange}%`}>
                    <span>▲</span>
                    <span>{Math.abs(Number(metalQuotes.gold.pctChange)).toFixed(2)}%</span>
                  </span>
                ) : (
                  <span className="text-rose-400 flex items-center text-[9px] gap-0.5" title={`Queda de ${metalQuotes.gold.pctChange}%`}>
                    <span>▼</span>
                    <span>{Math.abs(Number(metalQuotes.gold.pctChange)).toFixed(2)}%</span>
                  </span>
                )}
              </div>
              
              {/* Divisor */}
              <div className="w-[1px] h-3.5 bg-slate-700"></div>

              {/* Prata */}
              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 font-black">Prata:</span>
                <span className="text-slate-200 font-mono">R$ {metalQuotes.silver.price.toFixed(2)}/g</span>
                {Number(metalQuotes.silver.pctChange) >= 0 ? (
                  <span className="text-emerald-400 flex items-center text-[9px] gap-0.5" title={`Alta de ${metalQuotes.silver.pctChange}%`}>
                    <span>▲</span>
                    <span>{Math.abs(Number(metalQuotes.silver.pctChange)).toFixed(2)}%</span>
                  </span>
                ) : (
                  <span className="text-rose-400 flex items-center text-[9px] gap-0.5" title={`Queda de ${metalQuotes.silver.pctChange}%`}>
                    <span>▼</span>
                    <span>{Math.abs(Number(metalQuotes.silver.pctChange)).toFixed(2)}%</span>
                  </span>
                )}
              </div>

              {/* Divisor */}
              {metalQuotes.usd && <div className="w-[1px] h-3.5 bg-slate-700"></div>}

              {/* Dólar */}
              {metalQuotes.usd && (
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-500 font-black">Dólar:</span>
                  <span className="text-slate-200 font-mono">R$ {metalQuotes.usd.price.toFixed(2)}</span>
                  {Number(metalQuotes.usd.pctChange) >= 0 ? (
                    <span className="text-emerald-400 flex items-center text-[9px] gap-0.5" title={`Alta de ${metalQuotes.usd.pctChange}%`}>
                      <span>▲</span>
                      <span>{Math.abs(Number(metalQuotes.usd.pctChange)).toFixed(2)}%</span>
                    </span>
                  ) : (
                    <span className="text-rose-400 flex items-center text-[9px] gap-0.5" title={`Queda de ${metalQuotes.usd.pctChange}%`}>
                      <span>▼</span>
                      <span>{Math.abs(Number(metalQuotes.usd.pctChange)).toFixed(2)}%</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <OperatorSwitcher />

          {/* ── BOTÕES DA CENTRAL DE COMUNICAÇÃO ── */}
          {openDrawer && (
            <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-slate-700/60 shrink-0">
              {/* Chat / Conversas */}
              <button
                onClick={() => openDrawer("chat")}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition relative cursor-pointer"
                title="Conversas e Canais de Comunicação"
              >
                <MessageSquare size={16} />
                {unreadChatCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-rose-600 text-white rounded-full text-[9px] font-black flex items-center justify-center shadow-xs animate-bounce">
                    {unreadChatCount > 99 ? "99+" : unreadChatCount}
                  </span>
                )}
              </button>

              {/* Sino de Notificações */}
              <button
                onClick={() => openDrawer("notifications")}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition relative cursor-pointer"
                title="Central de Notificações e Alertas"
              >
                <Bell size={16} />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 px-1 min-w-[16px] h-4 bg-rose-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center animate-bounce">
                    {unreadNotifications}
                  </span>
                )}
              </button>

              {/* Tarefas & Prazos */}
              <button
                onClick={() => openDrawer("tasks")}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition relative cursor-pointer"
                title="Tarefas e Prazos"
              >
                <Calendar size={16} />
                {pendingTasksCount > 0 && (
                  <span className="absolute -top-1 -right-1 px-1 min-w-[16px] h-4 bg-amber-500 text-slate-950 rounded-full text-[9px] font-bold flex items-center justify-center">
                    {pendingTasksCount}
                  </span>
                )}
              </button>
            </div>
          )}

          {/* ── Usuário ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 shrink-0 border-l border-slate-700 pl-3">
            <div className="hidden sm:flex flex-col text-right whitespace-nowrap">
              <span className="text-[9px] text-slate-400">Olá,</span>
              <span className="font-bold text-xs truncate max-w-[100px]">{user?.name}</span>
              {user?.role === "master" && (
                <span className="text-[8px] bg-yellow-500 text-black px-1 rounded
                               font-bold w-fit ml-auto mt-0.5">MASTER</span>
              )}
            </div>
            <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center
                            justify-center text-slate-400 overflow-hidden border border-slate-600">
              {user?.avatar ? (
                <img
                  src={pb.files.getURL(user, user.avatar)} alt="Avatar"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <User size={14} />
              )}
            </div>
            <button onClick={logout} title="Sair"
              className="p-1.5 text-slate-400 hover:text-red-500
                         hover:bg-slate-800 rounded transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* ══ MOBILE DRAWER ════════════════════════════════════════════ */}

      {/* Backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[60] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Painel do Drawer */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-slate-900 z-[70]
          flex flex-col shadow-2xl lg:hidden transition-transform duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Header do drawer */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-white rounded flex items-center justify-center overflow-hidden">
              <img src="https://www.semprejoias.com.br/favicon/34692/sjoiasfav.png"
                   alt="Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="font-bold text-sm text-slate-100">Gestão de Processos</p>
              <p className="text-[10px] text-slate-500">Sempre Joias v1.1</p>
            </div>
          </div>
          <button onClick={() => setMobileOpen(false)}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        {/* Atalhos Rápidos de Comunicação no Mobile */}
        {openDrawer && (
          <div className="grid grid-cols-3 gap-2 p-3 bg-slate-800/60 border-b border-slate-800">
            <button
              onClick={() => {
                setMobileOpen(false);
                openDrawer("chat");
              }}
              className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 relative text-xs font-semibold"
            >
              <MessageSquare size={16} className="mb-1 text-indigo-400" />
              <span>Chat</span>
              {unreadChatCount > 0 && (
                <span className="absolute top-1 right-2 min-w-[16px] h-4 px-1 bg-rose-600 text-white rounded-full text-[9px] font-black flex items-center justify-center shadow-xs">
                  {unreadChatCount > 99 ? "99+" : unreadChatCount}
                </span>
              )}
            </button>

            <button
              onClick={() => {
                setMobileOpen(false);
                openDrawer("notifications");
              }}
              className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 relative text-xs font-semibold"
            >
              <Bell size={16} className="mb-1 text-rose-400" />
              <span>Avisos</span>
              {unreadNotifications > 0 && (
                <span className="absolute top-1 right-2 px-1 min-w-[16px] h-4 bg-rose-500 text-white rounded-full text-[9px] font-bold flex items-center justify-center">
                  {unreadNotifications}
                </span>
              )}
            </button>

            <button
              onClick={() => {
                setMobileOpen(false);
                openDrawer("tasks");
              }}
              className="flex flex-col items-center justify-center p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 relative text-xs font-semibold"
            >
              <Calendar size={16} className="mb-1 text-amber-400" />
              <span>Tarefas</span>
              {pendingTasksCount > 0 && (
                <span className="absolute top-1 right-2 px-1 min-w-[16px] h-4 bg-amber-500 text-slate-950 rounded-full text-[9px] font-bold flex items-center justify-center">
                  {pendingTasksCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Navegação do drawer */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <button
            onClick={() => handleTabChange("home")}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
              text-sm font-semibold mb-2 transition-all
              ${activeTab === "home"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
          >
            <Home size={16} /> Início
          </button>

          {canAccess("pdv") && (
            <button
              onClick={() => handleTabChange("pdv")}
              className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                text-sm font-semibold mb-2 transition-all
                ${activeTab === "pdv"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
            >
              <ShoppingBag size={16} /> PDV Showroom
            </button>
          )}

          {visibleGroups.map((g) => (
            <div key={g.id} className="mb-4">
              <p className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5"
                 style={{ color: g.color }}>{g.label}</p>
              {g.tabs.map((tabId) => {
                const Icon = TAB_ICONS[tabId] || ClipboardList;
                const isTabActive = activeTab === tabId;
                return (
                  <button key={tabId}
                    onClick={() => handleTabChange(tabId)}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                      text-sm font-semibold mb-0.5 transition-all
                      ${isTabActive
                        ? "bg-blue-600 text-white"
                        : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
                  >
                    <Icon size={15} style={isTabActive ? {} : { color: g.color, opacity: 0.7 }} />
                    {FULL_TAB_LABELS[tabId]}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer do drawer */}
        <div className="border-t border-slate-800 p-3 flex flex-col gap-1">
          {metalQuotes && (
            <div className="flex items-center justify-between bg-slate-800/80 px-2 py-2.5 rounded-lg border border-slate-750/50 mb-3 text-[10px] font-bold animate-fade-in">
              {/* Ouro */}
              <div className="flex flex-col items-center flex-1 border-r border-slate-700/50 pr-1">
                <span className="text-yellow-500 text-[9px] uppercase tracking-wider font-extrabold">Ouro</span>
                <span className="text-slate-200 font-mono mt-0.5 text-[10px]">R$ {metalQuotes.gold.price.toFixed(2)}</span>
                {Number(metalQuotes.gold.pctChange) >= 0 ? (
                  <span className="text-emerald-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                    <span>▲</span>
                    <span>{Math.abs(Number(metalQuotes.gold.pctChange)).toFixed(2)}%</span>
                  </span>
                ) : (
                  <span className="text-rose-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                    <span>▼</span>
                    <span>{Math.abs(Number(metalQuotes.gold.pctChange)).toFixed(2)}%</span>
                  </span>
                )}
              </div>
              
              {/* Prata */}
              <div className="flex flex-col items-center flex-1 border-r border-slate-700/50 px-1">
                <span className="text-slate-400 text-[9px] uppercase tracking-wider font-extrabold">Prata</span>
                <span className="text-slate-200 font-mono mt-0.5 text-[10px]">R$ {metalQuotes.silver.price.toFixed(2)}</span>
                {Number(metalQuotes.silver.pctChange) >= 0 ? (
                  <span className="text-emerald-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                    <span>▲</span>
                    <span>{Math.abs(Number(metalQuotes.silver.pctChange)).toFixed(2)}%</span>
                  </span>
                ) : (
                  <span className="text-rose-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                    <span>▼</span>
                    <span>{Math.abs(Number(metalQuotes.silver.pctChange)).toFixed(2)}%</span>
                  </span>
                )}
              </div>

              {/* Dólar */}
              {metalQuotes.usd && (
                <div className="flex flex-col items-center flex-1 pl-1">
                  <span className="text-emerald-500 text-[9px] uppercase tracking-wider font-extrabold">Dólar</span>
                  <span className="text-slate-200 font-mono mt-0.5 text-[10px]">R$ {metalQuotes.usd.price.toFixed(2)}</span>
                  {Number(metalQuotes.usd.pctChange) >= 0 ? (
                    <span className="text-emerald-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                      <span>▲</span>
                      <span>{Math.abs(Number(metalQuotes.usd.pctChange)).toFixed(2)}%</span>
                    </span>
                  ) : (
                    <span className="text-rose-400 text-[8px] flex items-center gap-0.5 mt-0.5">
                      <span>▼</span>
                      <span>{Math.abs(Number(metalQuotes.usd.pctChange)).toFixed(2)}%</span>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          {canAccess("config") && (
            <button
              onClick={() => handleTabChange("config")}
              className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg
                text-sm font-semibold transition-all
                ${activeTab === "config"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
            >
              <Settings size={15} /> Configurações
            </button>
          )}
          {(hasAccess("stock") || hasAccess("stock_production") || hasAccess("planejamento")) && (
            <button
              onClick={() => {
                toggleModoClassico(!modoClassico);
                setMobileOpen(false);
              }}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-semibold transition-all text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              {modoClassico ? <ToggleRight size={16} /> : <ToggleLeft size={16} className="text-emerald-400" />}
              <span>{modoClassico ? "Ativar Área Integrada" : "Modo Clássico"}</span>
            </button>
          )}
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center
                            justify-center overflow-hidden border border-slate-600 shrink-0">
              {user?.avatar ? (
                <img src={pb.files.getURL(user, user.avatar)} alt="Avatar"
                     className="w-full h-full object-cover"
                     onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <User size={13} className="text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-100 truncate">{user?.name}</p>
              {user?.role === "master" && (
                <span className="text-[9px] bg-yellow-500 text-black px-1.5 rounded font-bold">
                  MASTER
                </span>
              )}
            </div>
            <button onClick={logout}
              className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
